import { useEffect, useState } from "react";

import { duration, prefersReducedMotion } from "./motion";

/**
 * Общий элемент между экранами (FE-13).
 *
 * Карточка робота в каталоге и карточка на его экране — один и тот же
 * предмет, и переход между экранами должен показывать именно это: карточка
 * не гаснет, чтобы возникнуть заново, а перетекает.
 *
 * Почему имя перехода назначается в момент нажатия, а не постоянно: имя
 * `view-transition-name` выносит элемент из общего слепка страницы в
 * собственную группу. Если пометить все строки каталога, каждая начнёт
 * анимироваться сама по себе и уедет из общего движения экрана. Поэтому имя
 * получает ровно одна пара — та, по которой пользователь нажал.
 *
 * Имя обязано быть уникальным в документе: две одинаковые пометки заставят
 * браузер молча отменить весь переход.
 */
let pending: string | undefined;

/** Пометить исходный элемент. Вызывается в `onClick`, до навигации. */
export function markSharedElement(element: HTMLElement | null, name: string): void {
  if (!element) return;
  if (typeof document === "undefined" || typeof document.startViewTransition !== "function") return;
  if (prefersReducedMotion()) return;

  pending = name;
  element.style.viewTransitionName = name;

  // Снимаем имя даже если перехода не случилось (навигацию отменили,
  // браузер её пропустил): иначе следующая пара упрётся в дубликат.
  window.setTimeout(() => {
    element.style.viewTransitionName = "";
    if (pending === name) pending = undefined;
  }, duration("page") + 120);
}

/**
 * Имя для элемента на экране назначения. Непустое только тогда, когда переход
 * начали именно с этой пары, — иначе элемент участвует в общем движении
 * страницы наравне с остальными.
 */
export function useSharedElementName(name: string): string | undefined {
  const [matched, setMatched] = useState(() => pending === name);

  useEffect(() => {
    if (!matched) return;

    const timer = window.setTimeout(() => {
      setMatched(false);
    }, duration("page") + 120);

    return () => {
      window.clearTimeout(timer);
    };
  }, [matched]);

  return matched ? name : undefined;
}
